import { useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { v4 as uuidv4 } from "uuid";
import {
  OmsPresetMetadata,
  OmsPresetRequest,
  OmsProduct,
  Stage,
} from "../lusca-search-api/generated-src";
import { OmsReportsApiFactory } from "../lusca-search-api/LuscaSearchApiFactory";
import {
  OmsPresetRetryIdentity,
  presetRetryExpired,
  PresetRetryExpiredError,
} from "./omsPresetRetry";

const presetRequestOptions = { timeout: 15_000 };

export function useOmsExportCapabilities() {
  return useQuery({
    queryKey: ["oms-export-capabilities"],
    staleTime: 60_000,
    retry: false,
    queryFn: async () =>
      (
        await OmsReportsApiFactory().getOmsExportCapabilities(
          presetRequestOptions
        )
      ).data,
  });
}

export function presetValidationSourceUnavailable(error: unknown): boolean {
  const response = (
    error as {
      response?: { status?: number; data?: { error?: string } };
    }
  )?.response;
  return (
    response?.status === 404 &&
    response.data?.error === "Validation report snapshot not found"
  );
}

export function presetErrorMessage(error: unknown): string {
  if (error instanceof PresetRetryExpiredError) return error.message;
  const response = (
    error as {
      response?: { status?: number; data?: { error?: string } };
    }
  )?.response;
  if (
    [400, 409, 412, 413].includes(response?.status || 0) &&
    response?.data?.error
  )
    return response.data.error;
  if (presetValidationSourceUnavailable(error))
    return "The source report is no longer available. Return to the export, use the newer report and review your settings before saving.";
  if (response?.status === 404)
    return "This preset is no longer available. Your current settings are preserved.";
  return "Presets are temporarily unavailable. Retry with your settings retained.";
}

type PresetChange =
  | { action: "save"; body: OmsPresetRequest; existing?: OmsPresetMetadata }
  | { action: "rename"; existing: OmsPresetMetadata; name: string }
  | { action: "delete"; existing: OmsPresetMetadata };

export default function useOmsPresets(
  product: OmsProduct,
  enabled: boolean,
  retryRequest?: OmsPresetRetryIdentity
) {
  const queryClient = useQueryClient();
  const key = ["oms-presets", product];
  const retryIdentity = useRef(retryRequest);
  const [, setRetryIdentity] = useState(retryRequest);
  function rememberAttempt(request?: OmsPresetRetryIdentity) {
    retryIdentity.current = request;
    // Ref changes alone need not render before an asynchronous mutation settles.
    setRetryIdentity(request);
  }
  const list = useQuery({
    queryKey: key,
    enabled,
    staleTime: 30_000,
    retry: false,
    queryFn: async () => {
      const records: OmsPresetMetadata[] = [];
      let cursor: string | undefined;
      // An exact-limit DynamoDB page can have a cursor followed by an empty page.
      for (let page = 0; page < 3; page += 1) {
        const request = OmsReportsApiFactory().listOmsExportPresets(
          product,
          Stage.Prod,
          25,
          cursor,
          presetRequestOptions
        );
        // eslint-disable-next-line no-await-in-loop -- Each page needs the preceding server cursor.
        const result = await request;
        records.push(...result.data.presets);
        if (records.length > 50)
          throw new Error("Preset list exceeds its supported size");
        cursor = result.data.nextCursor;
        if (!cursor)
          return records.sort((a, b) => a.name.localeCompare(b.name));
      }
      throw new Error("Preset list changed while loading. Retry.");
    },
  });
  const read = useMutation({
    mutationFn: async (presetId: string) => {
      const preset = (
        await OmsReportsApiFactory().getOmsExportPreset(
          product,
          Stage.Prod,
          presetId,
          presetRequestOptions
        )
      ).data;
      if (preset.formatVersion !== 1)
        throw new Error("Unsupported preset format");
      return preset;
    },
  });
  const change = useMutation({
    mutationFn: async (input: PresetChange) => {
      const previous = retryIdentity.current;
      if (previous && presetRetryExpired(previous)) {
        let reconciled = false;
        let listRefreshed = false;
        try {
          await list.refetch({ throwOnError: true });
          listRefreshed = true;
          // Revision checks still apply to a later explicitly authorized mutation.
          if (input.existing) {
            await OmsReportsApiFactory().getOmsExportPreset(
              product,
              Stage.Prod,
              input.existing.presetId,
              presetRequestOptions
            );
          }
          reconciled = true;
        } catch (error) {
          reconciled =
            listRefreshed &&
            (error as { response?: { status?: number } })?.response?.status ===
              404;
        }
        throw new PresetRetryExpiredError(previous.id, reconciled);
      }
      const identity = JSON.stringify([product, input]);
      const attempt =
        retryIdentity.current?.identity === identity
          ? retryIdentity.current
          : { identity, id: uuidv4(), attemptedAt: Date.now() };
      if (retryIdentity.current !== attempt) rememberAttempt(attempt);
      const request = attempt.id;
      const api = OmsReportsApiFactory();
      if (input.action === "save") {
        const preset = input.existing
          ? await api.updateOmsExportPreset(
              product,
              Stage.Prod,
              input.existing.presetId,
              request,
              `"${input.existing.revision}"`,
              input.body,
              presetRequestOptions
            )
          : await api.createOmsExportPreset(
              product,
              Stage.Prod,
              request,
              input.body,
              presetRequestOptions
            );
        return { preset: preset.data };
      }
      if (input.action === "rename")
        return {
          preset: (
            await api.renameOmsExportPreset(
              product,
              Stage.Prod,
              input.existing.presetId,
              request,
              `"${input.existing.revision}"`,
              { name: input.name },
              presetRequestOptions
            )
          ).data,
        };
      await api.deleteOmsExportPreset(
        product,
        Stage.Prod,
        input.existing.presetId,
        request,
        `"${input.existing.revision}"`,
        presetRequestOptions
      );
      return { deleted: input.existing.presetId };
    },
    onSuccess: (_result, input) => {
      if (retryIdentity.current?.identity === JSON.stringify([product, input]))
        rememberAttempt();
      queryClient.invalidateQueries({ queryKey: key }).catch(() => undefined);
    },
  });
  return {
    list,
    read,
    change,
    changeIdentity: () => retryIdentity.current,
    restartExpiredRequest: () => {
      const { error } = change;
      if (
        !(error instanceof PresetRetryExpiredError) ||
        !error.reconciled ||
        retryIdentity.current?.id !== error.requestId
      )
        return;
      rememberAttempt();
      change.reset();
    },
  };
}

export type OmsPresetsState = ReturnType<typeof useOmsPresets>;
